import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Text,
  Spinner,
  Stack,
  Input,
  Button,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Badge,
  Icon,
} from '@chakra-ui/react';
import {
  FiUser,
  FiSearch,
  FiChevronRight,
  FiPhone,
  FiCalendar,
} from 'react-icons/fi';

const PatientAvatar = ({ gender }) => {
  const bg = useColorModeValue('blue.50', 'blue.900');
  const color = useColorModeValue('blue.500', 'blue.200');

  return (
    <Flex
      w="48px"
      h="48px"
      borderRadius="full"
      bg={bg}
      align="center"
      justify="center"
      color={color}
      boxShadow="inner"
    >
      <FiUser size={24} />
    </Flex>
  );
};

const PatientListModal = ({
  isOpen,
  onClose,
  patients = [],
  onSelectPatient,
  isLoading = false,
  searchValue,
  onSearchChange,
  page,
  setPage,
  totalPages = 1,
}) => {
  // Theme-aware colors
  const modalBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.600');
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const accentColor = 'blue.500';
  const bottleBg = useColorModeValue('gray.800', 'white');

  const handleSelect = (patient) => {
    onSelectPatient(patient);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: 'xl' }}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(4px)" />

      <ModalContent
        borderRadius="xl"
        bg={modalBg}
        overflow="hidden"
        boxShadow="2xl"
      >
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} py={4}>
          <Text
            fontSize="xl"
            fontWeight="bold"
            color={useColorModeValue('gray.700', 'white')}
          >
            Select Patient
          </Text>
        </ModalHeader>
        <ModalCloseButton top="14px" />

        <ModalBody p={6}>
          <InputGroup mb={6}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, ID or mobile..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              borderRadius="lg"
              focusBorderColor={accentColor}
              bg={useColorModeValue('gray.50', 'gray.900')}
              _placeholder={{ color: 'gray.400' }}
            />
          </InputGroup>

          {isLoading ? (
            <Flex
              justify="center"
              align="center"
              direction="column"
              py={20}
              gap={4}
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color={accentColor}
                size="xl"
              />
              <Text color={mutedText} fontWeight="medium">
                Fetching patients...
              </Text>
            </Flex>
          ) : patients.length === 0 ? (
            <Flex direction="column" align="center" py={12}>
              <Text fontSize="lg" fontWeight="medium" color={mutedText}>
                No patients found
              </Text>
            </Flex>
          ) : (
            <Stack spacing={3}>
              {patients.map((patient) => (
                <Box
                  key={patient.id}
                  p={4}
                  bg={cardBg}
                  borderLeftWidth="4px"
                  borderLeftColor="transparent"
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                  cursor="pointer"
                  transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
                  boxShadow="sm"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                    bg: hoverBg,
                    borderLeftColor: accentColor,
                  }}
                  onClick={() => handleSelect(patient)}
                >
                  <Flex align="center" gap={4}>
                    <PatientAvatar />

                    <Box flex="1">
                      <Flex justify="space-between" align="baseline" mb={1}>
                        <Text
                          textTransform={'capitalize'}
                          fontWeight="bold"
                          fontSize="md"
                          color={bottleBg}
                        >
                          {patient.name}
                        </Text>
                        <Badge
                          variant="subtle"
                          colorScheme="gray"
                          fontSize="xs"
                          borderRadius="full"
                          px={2}
                        >
                          #{patient.patientId}
                        </Badge>
                      </Flex>

                      <Flex gap={4} wrap="wrap">
                        <Flex align="center" gap={1}>
                          <Icon as={FiPhone} boxSize={3} color={mutedText} />
                          <Text
                            fontSize="xs"
                            color={mutedText}
                            fontWeight="medium"
                          >
                            {patient.mobile || 'No Phone'}
                          </Text>
                        </Flex>
                        <Flex align="center" gap={1}>
                          <Icon as={FiCalendar} boxSize={3} color={mutedText} />
                          <Text
                            fontSize="xs"
                            color={mutedText}
                            fontWeight="medium"
                          >
                            {patient.age ? `${patient.age} Years` : 'Age N/A'}
                          </Text>
                        </Flex>
                      </Flex>
                    </Box>

                    <Icon as={FiChevronRight} color="gray.300" />
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}

          {totalPages > 1 && (
            <Flex
              mt={8}
              justify="space-between"
              align="center"
              pt={4}
              borderTopWidth="1px"
              borderColor={borderColor}
            >
              <Button
                variant="ghost"
                size="sm"
                leftIcon={
                  <FiChevronRight style={{ transform: 'rotate(180deg)' }} />
                }
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                isDisabled={page === 1}
              >
                Previous
              </Button>

              <Text
                fontSize="xs"
                fontWeight="bold"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Page {page} of {totalPages}
              </Text>

              <Button
                variant="ghost"
                size="sm"
                rightIcon={<FiChevronRight />}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                isDisabled={page === totalPages}
              >
                Next
              </Button>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PatientListModal;
